import { Avatar, Button, Flex, Image, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react'
import React, { useState } from 'react'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { FaCheck, FaChevronDown, FaChevronUp, FaEye, FaRegPlayCircle, FaRegStopCircle, FaStreetView, FaTrashAlt } from 'react-icons/fa'
import { FiEdit } from 'react-icons/fi'
import { IoPersonAddSharp } from 'react-icons/io5'
import { MdBlock } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { ButtonCmp } from '../../../../../../components/ButtonCmp'
import { ElementoTooltipCmp } from '../../../../../../components/ElementoTooltipCmp'
import { useAuth } from '../../../../../../contexts/AuthContext'
import { IEmployee } from '../../../../../../interfaces'
import { parseJwt } from '../../../../../../services/token'
import { PropInvalida } from '../../../../../../Utils/Helper'

import {
  CargoColumn,
  ColaboradorColumn,
  Container,
  PerfilColumn
} from './styles'

interface iTabelaColaboradoresProps {
  employeeList: IEmployee[]
  ordenacao?: boolean
  desligados?: boolean
  ultimoFiltro?: number
  onChangeGestao: (employee: IEmployee) => void
  onEdit: (employee: IEmployee) => void
  onDelete: (employee: IEmployee) => void
  onInativar: (id: string) => void
  onReativar: (id: string) => void
  onOrdenar: (filtro: number, ascendente: boolean) => void
  onRealocar: (id: string) => void
}

const estiloMenuItem = {
  color: 'var(--Gray1)',
  backgroundColor: 'var(--c1)',
  _focus: { backgroundColor: 'var(--c1)' },
  _hover: { backgroundColor: 'var(--Azul)', color: '#ffff' }
}

export const TabelaColaboradores: React.FC<iTabelaColaboradoresProps> = ({
  employeeList,
  onChangeGestao,
  onEdit,
  ordenacao,
  ultimoFiltro,
  onDelete,
  onOrdenar,
  desligados,
  onRealocar,
  onInativar,
  onReativar
}) => {
  const [Ordenacao, setOrdenacao] = useState(ordenacao ?? false)
  const [UltimoFiltro, setUltimoFiltro] = useState<number>(ultimoFiltro ?? 1)

  const { Permissoes } = useAuth()
  const nav = useNavigate()

  const podeVisualizarPerfil = Permissoes.jornada || Permissoes.desempenho
  const isAdm = parseJwt().adm === 'True'
  const exibirColunaPerfil = employeeList.some(e => e.perfilPI)

  function OrdenarTabela(filtro: number): void {
    if (!(!Ordenacao && UltimoFiltro !== filtro)) {
      setOrdenacao(!Ordenacao)
    }
    setUltimoFiltro(filtro)
    onOrdenar(filtro, !(!Ordenacao && UltimoFiltro !== filtro) ? !Ordenacao : Ordenacao)
  }

  function ReturnIcon(filtro: number): any {
    return UltimoFiltro === filtro && !Ordenacao ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />
  }

  function VisualizarPerfil(employee: IEmployee): void {
    if (!podeVisualizarPerfil) return
    nav(`/Rh/VisualizarPerfil/${employee.id}${employee.dataDesligamento ? '?criar=true' : ''}`)
  }

  return (
    <Container>
      <table>
        <thead>
          <tr>
            <th><Flex alignItems={'center'} gap={'.375rem'} cursor={'pointer'} onClick={() => OrdenarTabela(1)}>Colaborador {ReturnIcon(1)}</Flex></th>
            <th><Flex alignItems={'center'} gap={'.375rem'} cursor={'pointer'} onClick={() => OrdenarTabela(3)}>Cargo / Área {ReturnIcon(3)}</Flex></th>
            {exibirColunaPerfil && <th>Perfil</th>}
            <th><Flex alignItems={'center'} gap={'.375rem'} cursor={'pointer'} onClick={() => OrdenarTabela(5)}>Gestor {ReturnIcon(5)}</Flex></th>
            <th>Ações</th>
          </tr>
        </thead>

        <tbody>
          {employeeList.map((employee) => {
            const ativo = PropInvalida(employee.dataDesligamento) && !desligados && !employee.inativo
            const ehGestor = employee.gestorDeArea || employee.gestor
            return (
              <tr key={employee.id} style={{ opacity: employee.inativo ? '.75' : undefined, background: employee.inativo ? 'var(--c2)' : undefined }}>
                <ColaboradorColumn
                  style={{ borderLeft: employee.primeiroAcesso ? '3px solid var(--Azul)' : '3px solid var(--Orange)' }}
                  onClick={() => VisualizarPerfil(employee)}
                >
                  <Flex alignItems={'center'} gap={'.75rem'}>
                    <Avatar
                      src={employee.avatar}
                      name={employee.nome}
                      h={'2.75rem'}
                      w={'2.75rem'}
                    />
                    <Flex flexDirection={'column'}>
                      <strong>{employee.nome} {employee.sobrenome}</strong>
                      <span>{employee.email}</span>
                    </Flex>
                  </Flex>
                </ColaboradorColumn>

                <CargoColumn onClick={() => VisualizarPerfil(employee)}>
                  <Flex flexDirection={'column'}>
                    <strong>{employee.cargo?.nome}</strong>
                    <span>{employee.departamento?.nome}</span>
                  </Flex>
                </CargoColumn>

                {exibirColunaPerfil &&
                  <PerfilColumn>
                    {employee.perfilPI && (
                      <Flex alignItems={'center'} gap={'.75rem'}>
                        <ElementoTooltipCmp label={employee.perfilPI.nomeSelo}>
                          <Image
                            src={employee.perfilPI.urlSelo}
                            alt={employee.perfilPI.nomeSelo}
                            h={'2.5rem'}
                            w={'2.5rem'}
                            minW={'2.5rem'}
                            objectFit={'contain'}
                          />
                        </ElementoTooltipCmp>
                        <Image
                          src={employee.perfilPI.urlSelf}
                          alt={`Padrão comportamental de ${employee.nome}`}
                          w={'150px'}
                          minW={'150px'}
                          objectFit={'contain'}
                        />
                      </Flex>
                    )}
                  </PerfilColumn>
                }

                <td>
                  <Flex
                    alignItems={'center'}
                    justifyContent={'center'}
                    gap={'.375rem'}
                    w={'fit-content'}
                    borderRadius={'1rem'}
                    padding={'.25rem .75rem'}
                    background={ehGestor ? 'var(--Green2OpacoBg)' : 'var(--c6OpacoBg)'}
                    color={ehGestor ? 'var(--Green2)' : 'var(--c7)'}
                    fontWeight={600}
                    fontSize={'.813rem'}
                  >
                    <FaCheck size={11} /> {ehGestor ? 'Sim' : 'Não'}
                  </Flex>
                </td>

                <td>
                  <Flex alignItems={'center'} justifyContent={'flex-end'} gap={'.5rem'}>
                    {employee.inativo &&
                      <Text fontSize={'.813rem'} color={'var(--Orange)'} fontWeight={700}>Colaborador inativo</Text>
                    }

                    {(!PropInvalida(employee.dataDesligamento) || desligados) &&
                      <ElementoTooltipCmp bg={'var(--Red)'} label={!PropInvalida(employee.dataDesligamento) ? `Colaborador ${desligados ? '' : 'será'} desligado no dia ${employee.dataDesligamento ?? ''}` : 'Colaborador desligado'}>
                        <MdBlock style={{ opacity: '50%', color: 'red' }} size={18} />
                      </ElementoTooltipCmp>
                    }

                    {podeVisualizarPerfil &&
                      <ButtonCmp small OutlineColor='c7' leftIcon={<FaEye />} onClick={() => VisualizarPerfil(employee)}>Ver</ButtonCmp>
                    }

                    {isAdm && ativo &&
                      <ButtonCmp small OutlineColor='c7' leftIcon={<FiEdit />} onClick={() => onEdit(employee)}>Editar</ButtonCmp>
                    }

                    {isAdm &&
                      <Menu placement={'bottom-end'}>
                        <MenuButton
                          as={Button}
                          padding={'0px'}
                          minW={'1.75rem'}
                          h={'1.75rem'}
                          color={'var(--c7)'}
                          backgroundColor={'transparent'}
                          _focus={{ backgroundColor: 'transparent' }}
                          _hover={{ backgroundColor: 'transparent', boxShadow: '0px 0px 5px rgb(0,0,0,0.3)' }}
                          _active={{ backgroundColor: 'transparent', boxShadow: '0px 0px 5px rgb(0,0,0,0.3)' }}
                        >
                          <BsThreeDotsVertical />
                        </MenuButton>
                        <MenuList paddingY={'10px'} boxShadow={'0px 0px 5px rgb(0,0,0,0.3)'} borderRadius={'8px'} zIndex={999}>
                          {ativo && podeVisualizarPerfil &&
                            <MenuItem {...estiloMenuItem} icon={<FaStreetView />} onClick={() => onChangeGestao(employee)}>
                              <span>Gerenciar respondentes</span>
                            </MenuItem>
                          }
                          {ativo &&
                            <MenuItem {...estiloMenuItem} icon={<FaRegStopCircle />} onClick={() => onInativar(employee.id)}>
                              <span>Inativar colaborador</span>
                            </MenuItem>
                          }
                          {PropInvalida(employee.dataDesligamento) && !desligados && employee.inativo &&
                            <MenuItem {...estiloMenuItem} icon={<FaRegPlayCircle />} onClick={() => onReativar(employee.id)}>
                              <span>Reativar colaborador</span>
                            </MenuItem>
                          }
                          {ativo &&
                            <MenuItem {...estiloMenuItem} icon={<FaTrashAlt />} onClick={() => onDelete(employee)}>
                              <span>Excluir colaborador</span>
                            </MenuItem>
                          }
                          {(!PropInvalida(employee.dataDesligamento) || desligados) &&
                            <MenuItem {...estiloMenuItem} icon={<IoPersonAddSharp />} onClick={() => onRealocar(employee.id)}>
                              <span>Realocar colaborador</span>
                            </MenuItem>
                          }
                        </MenuList>
                      </Menu>
                    }
                  </Flex>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Container>
  )
}
